"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.NumberFormatter = void 0;
const decimal_js_1 = __importDefault(require("decimal.js"));
const SafeDecimal_1 = require("./SafeDecimal");
const roundingMap = {
    up: decimal_js_1.default.ROUND_UP,
    down: decimal_js_1.default.ROUND_DOWN,
    ceil: decimal_js_1.default.ROUND_CEIL,
    floor: decimal_js_1.default.ROUND_FLOOR,
};
const coinMinPrecision = {
    BTC: 6,
    ETH: 5,
};
const formatterCacheMap = new Map();
function getFormatterCacheKey(locale, options) {
    return `${locale}-${options.minimumFractionDigits}-${options.maximumFractionDigits}-${options.style}-${options.signDisplay}-${options.currency}-${options.useGrouping}-${options.notation}-${options.compactDisplay}`;
}
var NumberFormatter;
(function (NumberFormatter) {
    function _getPrecision(num) {
        let decimal = 10;
        const numAbs = num.abs();
        if (numAbs.greaterThanOrEqualTo(1)) {
            decimal = 2; // 1.23
        }
        else if (numAbs.greaterThanOrEqualTo(0.1)) {
            decimal = 3; // 0.123
        }
        else if (numAbs.greaterThanOrEqualTo(0.01)) {
            decimal = 4; // 0.0123
        }
        else if (numAbs.greaterThanOrEqualTo(0.001)) {
            decimal = 5; // 0.00123
        }
        else if (numAbs.greaterThanOrEqualTo(0.0001)) {
            decimal = 6; // 0.000123
        }
        else if (numAbs.greaterThanOrEqualTo(0.00001)) {
            decimal = 7; // 0.0000123
        }
        else if (numAbs.greaterThanOrEqualTo(0.000001)) {
            decimal = 8; // 0.00000123
        }
        else if (numAbs.greaterThanOrEqualTo(0.0000001)) {
            decimal = 9; // 0.000000123
        }
        return decimal;
    }
    /**
     * 根据num值的大小自动计算精度: 数字>=100时为0，数字<0.0000001时为10，其他情况保留3位有效小数数字
     * @param num
     */
    function getPrecision(num) {
        return _getPrecision(new SafeDecimal_1.SafeDecimal(num));
    }
    NumberFormatter.getPrecision = getPrecision;
    /**
     * 获取法币的精度：当法币为USD&&quote为美元稳定币时，精度和quote相同，否则为自动精度
     * @param currency
     * @param quote
     * @param quotePrecision
     */
    function getFiatPrecision(currency = "", quote = "", quotePrecision) {
        if (currency === "USD" && ["USDT", "BUSD", "USDC"].includes(quote)) {
            return quotePrecision;
        }
        return undefined;
    }
    NumberFormatter.getFiatPrecision = getFiatPrecision;
    function toPercentShortInfo(shortInfo) {
        return { level: shortInfo.level / 100, offset: shortInfo.offset };
    }
    const shortInfoEn = [
        { level: 1000, offset: 3 },
        { level: 1000000, offset: 6 },
        { level: 1000000000, offset: 9 },
        { level: 1000000000000, offset: 12 },
    ];
    const percentShortInfoEn = shortInfoEn.map(toPercentShortInfo);
    const shortInfoZh = [
        { level: 10000, offset: 4 },
        { level: 100000000, offset: 8 },
        { level: 1000000000000, offset: 12 },
    ];
    const percentShortInfoZh = shortInfoZh.map(toPercentShortInfo);
    const shortInfoDe = [
        { level: 1000000, offset: 6 },
        { level: 1000000000, offset: 9 },
        { level: 1000000000000, offset: 12 },
    ];
    const percentShortInfoDe = shortInfoDe.map(toPercentShortInfo);
    const shortInfoKo = [
        { level: 1000, offset: 3 },
        { level: 10000, offset: 4 },
        { level: 100000000, offset: 8 },
        { level: 1000000000000, offset: 12 },
    ];
    const percentShortInfoKo = shortInfoKo.map(toPercentShortInfo);
    const offsetToDiv = [
        1, 10, 100, 1000, 10000, 100000, 1000000, 10000000, 100000000, 1000000000, 10000000000, 100000000000, 1000000000000, 10000000000000,
        100000000000000,
    ];
    const shortInfoMap = new Map([
        ["en", shortInfoEn],
        ["ar", shortInfoEn],
        ["fi", shortInfoEn],
        ["fr", shortInfoEn],
        ["id", shortInfoEn],
        ["ms", shortInfoEn],
        ["pt", shortInfoEn],
        ["ro", shortInfoEn],
        ["ru", shortInfoEn],
        ["th", shortInfoEn],
        ["tr", shortInfoEn],
        ["vi", shortInfoEn],
        ["zh-CN", shortInfoZh],
        ["zh-TW", shortInfoZh],
        ["ja", shortInfoZh],
        ["de", shortInfoDe],
        ["ko", shortInfoKo],
    ]);
    const percentShortInfoMap = new Map([
        ["en", percentShortInfoEn],
        ["ar", percentShortInfoEn],
        ["fi", percentShortInfoEn],
        ["fr", percentShortInfoEn],
        ["id", percentShortInfoEn],
        ["ms", percentShortInfoEn],
        ["pt", percentShortInfoEn],
        ["ro", percentShortInfoEn],
        ["ru", percentShortInfoEn],
        ["th", percentShortInfoEn],
        ["tr", percentShortInfoEn],
        ["vi", percentShortInfoEn],
        ["zh-CN", percentShortInfoZh],
        ["zh-TW", percentShortInfoZh],
        ["ja", percentShortInfoZh],
        ["de", percentShortInfoDe],
        ["ko", percentShortInfoKo],
    ]);
    /**
     * 执行精度格式化、舍入方式
     * @param num
     * @param precision
     * @param shortPrecision
     * @param rounding
     * @param percent
     * @param short
     * @param locale
     * @private
     */
    function toDecimalPlacesDecimal(num, precision, shortPrecision, rounding, percent, short, locale) {
        var _a;
        const decimalRounding = (_a = roundingMap[rounding || "floor"]) !== null && _a !== void 0 ? _a : decimal_js_1.default.ROUND_FLOOR;
        let numAbs = num.abs();
        let shorted = false;
        let shortOffset = 0;
        if (short) {
            const shortInfo = percent ? percentShortInfoMap.get(locale) : shortInfoMap.get(locale);
            if (shortInfo) {
                for (const item of shortInfo) {
                    if (numAbs.lessThan(item.level)) {
                        break;
                    }
                    shorted = true;
                    shortOffset = item.offset;
                }
            }
        }
        let offset = shortOffset;
        if (percent) {
            offset -= 2;
        }
        let numDecimal = num;
        const decimalPlaces = shorted ? shortPrecision : precision;
        if (decimalRounding === decimal_js_1.default.ROUND_DOWN ||
            (decimalRounding === decimal_js_1.default.ROUND_FLOOR && num.greaterThan(0)) ||
            (decimalRounding === decimal_js_1.default.ROUND_CEIL && num.lessThan(0))) {
            // rounding为直接舍去末尾的数字，使用字符串截取代替数学运算
            const roundingOffset = decimalPlaces - offset;
            const [integer, decimalStr = ""] = num.toFixed(20, decimal_js_1.default.ROUND_FLOOR).split(".");
            const decimal = decimalStr.replace(/0+$/, "");
            const integerLen = integer.length;
            const decimalLen = decimal.length;
            if (roundingOffset === 0) {
                numDecimal = new SafeDecimal_1.SafeDecimal(integer);
            }
            else if (roundingOffset < 0) {
                // 将整数的最后{roundingOffset}位替换为0
                numDecimal = new SafeDecimal_1.SafeDecimal(integer.substring(0, integerLen + roundingOffset).padEnd(integerLen, "0"));
            }
            else {
                // 有小数
                if (decimalLen <= roundingOffset) {
                    // 小数位数小于所需精度，无需截断
                    numDecimal = num;
                }
                else {
                    // 小数位数大于所需精度，将多余部分截断
                    numDecimal = new SafeDecimal_1.SafeDecimal(`${integer}.${decimal.substring(0, roundingOffset)}`);
                }
            }
        }
        else {
            if (offset === 0) {
                numDecimal = num.toDP(decimalPlaces, decimalRounding);
            }
            else if (offset > 0) {
                // 将小数点左移，按精度rounding之后，再右移
                const div = offsetToDiv[offset];
                numDecimal = num.div(div).toDP(decimalPlaces, decimalRounding).mul(div);
            }
            else {
                //将小数点右移，按精度rounding之后，再左移
                const mul = offsetToDiv[-offset];
                numDecimal = num.mul(mul).toDP(decimalPlaces, decimalRounding).div(mul);
            }
        }
        return { numDecimal, shorted };
    }
    /**
     * 将数字格式化为用于显示的字符串
     * @param value
     * @param options
     */
    function format(value = 0, options = {}) {
        const num = new SafeDecimal_1.SafeDecimal(value);
        const { shortPrecision = 2, rounding, short = false, grouping, locale = "en", style = "decimal", sign = "auto", currency = "USD", coin, } = options;
        const percent = style === "percent";
        let pad = options.pad;
        if (pad === undefined) {
            if (percent) {
                // 格式化为百分比时，默认保留小数末尾的0
                pad = true;
            }
            else {
                pad = false;
            }
        }
        // 确定小数位数
        let precision = options.precision;
        if (precision === undefined) {
            if (percent) {
                // 格式化为百分比时，默认2位精度
                precision = 2;
            }
        }
        if (precision === undefined) {
            precision = _getPrecision(num);
            if (percent) {
                precision = precision > 2 ? precision - 2 : 0;
            }
            else if (coin) {
                const minPrecision = coinMinPrecision[coin];
                if (minPrecision && precision < minPrecision) {
                    precision = minPrecision;
                }
            }
        }
        // 处理精度
        const { numDecimal, shorted } = toDecimalPlacesDecimal(num, precision, shortPrecision, rounding, percent, short, locale);
        if (shorted) {
            // 缩写了，使用shortPrecision精度
            precision = shortPrecision;
        }
        const numOptions = {
            minimumFractionDigits: pad ? precision : 0,
            maximumFractionDigits: precision,
            style: style,
            signDisplay: sign,
            currency: currency,
            useGrouping: grouping,
        };
        if (percent) {
            if (!options.sign) {
                if (value === "-0") {
                    numOptions.signDisplay = "auto";
                }
                else {
                    numOptions.signDisplay = "always";
                }
            }
        }
        if (short) {
            numOptions.notation = "compact";
            numOptions.compactDisplay = "short";
        }
        const key = getFormatterCacheKey(locale, numOptions);
        let formatter = formatterCacheMap.get(key);
        if (!formatter) {
            formatter = new Intl.NumberFormat(locale, numOptions);
            formatterCacheMap.set(key, formatter);
        }
        return formatter.format(numDecimal.toNumber());
    }
    NumberFormatter.format = format;
})(NumberFormatter || (exports.NumberFormatter = NumberFormatter = {}));
